import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ValdostaHero from './ValdostaHero';
import StayAtGlanceSection from './StayAtGlanceSection';
import './ValdostaProperty.css';
import { bookingHref, stayProperties } from './stayProperties';
import { localAttractions } from './localAttractions';
import { stayTimes } from './staySettings';
import StayRates from '../components/StayRates';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const exploreCards = [
  ['Time to unwind', 'Settle in and enjoy a slower pace.'],
  ['Family time', 'Room to gather, reset, and stay awhile.'],
  ['Local experiences', 'Explore the destination at your own pace.'],
  ['A fresh start', 'Settle into a place that makes the next chapter easier.'],
  ['Weekend escapes', 'Take the slower route and enjoy the little things.'],
  ['Everyday stays', 'Comfort, convenience, and a warm welcome.'],
];
export default function ValdostaProperty({ property = stayProperties.georgia }) {
  const scope = useRef(null);
  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(scope);
      gsap.from(q('.valdosta-property__explore-card, .valdosta-property__attractions li'), { autoAlpha: 0, y: 20, stagger: .08, duration: .65, scrollTrigger: { trigger: q('.valdosta-property__explore'), start: 'top 80%', once: true } });
      gsap.from(q('.valdosta-property__detail'), { autoAlpha: 0, y: 20, stagger: .1, duration: .65, scrollTrigger: { trigger: q('.valdosta-property__details'), start: 'top 82%', once: true } });
      gsap.from(q('.valdosta-property__cta'), { autoAlpha: 0, y: 24, scale: .985, duration: .8, scrollTrigger: { trigger: q('.valdosta-property__cta'), start: 'top 85%', once: true } });
    });
    return () => media.revert();
  }, { scope });

  return (
    <div ref={scope} className="valdosta-property">
      <Header homeHref="/" />
      <main id="main-content" tabIndex={-1}>
        <ValdostaHero property={property} />

        <StayAtGlanceSection property={property} />

        <section className="valdosta-property__explore" aria-labelledby="explore-title">
          <p className="valdosta-property__eyebrow">MAKE THE MOST OF WHERE YOU ARE</p>
          <h2 id="explore-title">A little exploring goes a long way.</h2>
          <div className="valdosta-property__explore-grid">
            {(property.explore || exploreCards).map(([title, copy], index) => <article className="valdosta-property__explore-card" key={title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{title}</h3><p>{copy}</p></article>)}
          </div>
          <div className="valdosta-property__attractions" id="local-attractions">
            <h3>Local places to explore</h3>
            <ul>{localAttractions[property.id].map(place => <li key={place.name}><h4><a href={place.mapUrl} target="_blank" rel="noopener noreferrer">{place.name} <span aria-hidden="true">↗</span></a></h4><a href={place.mapUrl} target="_blank" rel="noopener noreferrer">{place.linkText} <span className="valdosta-property__external">(new tab)</span></a></li>)}</ul>
            {!localAttractions[property.id].length && <p>Ask your host for nearby attractions and local recommendations.</p>}
          </div>
        </section>

        <section className="valdosta-property__details" aria-labelledby="details-title">
          <p className="valdosta-property__eyebrow">GOOD TO KNOW</p>
          <h2 id="details-title">The little details matter.</h2>
          <div className="valdosta-property__details-grid">
            <article className="valdosta-property__detail"><h3>House rules</h3><p>Standard check-in is {stayTimes.checkIn} and check-out is {stayTimes.checkOut}, in the property’s local time. Contact your host for the full house rules before booking.</p></article>
            <article className="valdosta-property__detail"><h3>Rates and cancellation</h3><StayRates property={property} /><p>Ask your host for cancellation terms before confirming your stay.</p></article>
            <article className="valdosta-property__detail"><h3>Questions for your host</h3><p>Reach out before your stay and we will be happy to help.</p><a href="/contact">Contact your host <span aria-hidden="true">↗</span></a></article>
          </div>
        </section>

        <section className="valdosta-property__cta" aria-labelledby="property-cta-title"><div><p className="valdosta-property__eyebrow">YOUR NEXT CHAPTER</p><h2 id="property-cta-title">{property.comingSoon ? 'A new stay is on the way.' : 'Ready for a change of scenery?'}</h2><p>{property.comingSoon ? 'Hillsboro, Oregon is coming soon. Booking is not available yet.' : 'Choose your place. Bring your plans. Make yourself at home.'}</p></div>{!property.comingSoon && <a href={bookingHref(property.id)}>Check dates <span aria-hidden="true">↗</span></a>}</section>
      </main>
      <Footer />
    </div>
  );
}
